// SPDX-License-Identifier: GPL-3.0-or-later
// Character reactions. When a feedback event names a character, the model pulses (a short scale
// swell) and bounces (a short rise). The change is on the mount of the character (the scene
// places and scales the mount). It always returns to the exact start values.
// With reduced motion (and with the effects off) a character does not move: THEME.md 11.3.

import type { Object3D } from "three";
import type { DecorationNode } from "../scene.ts";

/** One reaction lasts this long, in ms. */
export const REACTION_MS = 520;
/** A character reacts again after this time at the earliest (3 times in 1 s at most). */
export const REACTION_GAP_MS = 340;
/** Largest extra scale (a share of the base scale). */
export const PULSE_SCALE = 0.12;
/** Largest rise in table units. */
export const BOUNCE_UNITS = 14;

/** The reactions of all characters. */
export interface Reactions {
  /**
   * Start a reaction. Returns the mount of the character when it started, or null when the
   * character is unknown or still busy.
   */
  react(characterId: string | undefined, nowMs: number): Object3D | null;
  /** Move on. `enabled` false puts every character back to its base at once. */
  update(nowMs: number, enabled: boolean): void;
  /** Put every character back to its base. */
  reset(): void;
}

interface Tracked {
  readonly object: Object3D;
  readonly baseScale: readonly [number, number, number];
  readonly baseY: number;
  startedAt: number;
  active: boolean;
}

interface ReactionState {
  readonly byName: Map<string, Tracked>;
  readonly all: readonly Tracked[];
}

function track(node: DecorationNode): Tracked {
  const s = node.object.scale;
  return {
    object: node.object,
    baseScale: [s.x, s.y, s.z],
    baseY: node.object.position.y,
    startedAt: Number.NEGATIVE_INFINITY,
    active: false,
  };
}

function restore(t: Tracked): void {
  t.object.scale.set(t.baseScale[0], t.baseScale[1], t.baseScale[2]);
  t.object.position.y = t.baseY;
  t.active = false;
}

function pose(t: Tracked, phase: number): void {
  const swell = Math.sin(Math.PI * phase);
  const k = 1 + PULSE_SCALE * swell;
  t.object.scale.set(t.baseScale[0] * k, t.baseScale[1] * k, t.baseScale[2] * k);
  t.object.position.y = t.baseY + BOUNCE_UNITS * swell;
}

/** The tracked character with this id, or undefined. */
function reactionsLookup(st: ReactionState, characterId: string | undefined): Tracked | undefined {
  return characterId === undefined ? undefined : st.byName.get(characterId);
}

/** A character still in its gap after the last reaction cannot start another. */
function reactionsBusy(t: Tracked, nowMs: number): boolean {
  return t.active && nowMs - t.startedAt < REACTION_GAP_MS;
}

function reactionsReact(
  st: ReactionState,
  characterId: string | undefined,
  nowMs: number,
): Object3D | null {
  const t = reactionsLookup(st, characterId);
  if (!t || reactionsBusy(t, nowMs)) return null;
  t.startedAt = nowMs;
  t.active = true;
  return t.object;
}

/** Pose or restore one active character for the time `nowMs`. */
function reactionsStep(t: Tracked, nowMs: number, enabled: boolean): void {
  if (!t.active) return;
  const age = nowMs - t.startedAt;
  if (!enabled || age < 0 || age >= REACTION_MS) restore(t);
  else pose(t, age / REACTION_MS);
}

function reactionsUpdate(st: ReactionState, nowMs: number, enabled: boolean): void {
  for (const t of st.all) reactionsStep(t, nowMs, enabled);
}

function reactionsReset(st: ReactionState): void {
  for (const t of st.all) if (t.active) restore(t);
}

/** Make the reactions for the decorations of a scene. */
export function createReactions(nodes: readonly DecorationNode[]): Reactions {
  const byName = new Map<string, Tracked>();
  for (const node of nodes) byName.set(node.spec.name, track(node));
  const st: ReactionState = { byName, all: [...byName.values()] };

  return {
    react(characterId, nowMs) {
      return reactionsReact(st, characterId, nowMs);
    },
    update(nowMs, enabled) {
      reactionsUpdate(st, nowMs, enabled);
    },
    reset() {
      reactionsReset(st);
    },
  };
}
